import { Plus, Search, Users } from 'lucide-react';
import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { Navigate } from 'react-router-dom';
import { api, ApiError } from '../api/client';
import type { RespondentGender, RespondentRecord } from '../api/types';
import { useAuth } from '../auth';
import { Topbar } from '../components/Layout';
import { Empty, Field, Modal, PageLoader, useToast } from '../components/ui';
import { useI18n } from '../i18n';

const genders: RespondentGender[] = ['female', 'male', 'nonbinary', 'self_described', 'prefer_not_to_say', 'unspecified'];

export function RespondentsPage() {
  const { can } = useAuth();
  const { t } = useI18n();
  const toast = useToast();
  const [rows, setRows] = useState<RespondentRecord[] | null>(null);
  const [query, setQuery] = useState('');
  const [editing, setEditing] = useState<RespondentRecord | null>(null);
  const [creating, setCreating] = useState(false);
  const canManage = can('respondents.manage');

  const load = useCallback(async () => {
    try { setRows((await api.respondents(query)).respondents); }
    catch (e) { toast(e instanceof ApiError ? e.message : t('respondents.load_failed'), 'error'); }
  }, [query, toast, t]);
  useEffect(() => { void load(); }, [load]);
  if (!canManage) return <Navigate to="/" replace />;

  return <>
    <Topbar title={t('respondents.title')}>
      <button className="btn primary" onClick={() => setCreating(true)}><Plus /> {t('respondents.add')}</button>
    </Topbar>
    <p className="sub mb-16">{t('respondents.subtitle')}</p>
    <div className="card">
      <div className="card-head">
        <h2 className="grow">{t('respondents.title')}</h2>
        <div className="search" style={{ width: 280, maxWidth: '100%' }}><Search />
          <input className="input" placeholder={t('respondents.search')} aria-label={t('respondents.search')} value={query} onChange={(e) => setQuery(e.target.value)} />
        </div>
      </div>
      {rows === null ? <PageLoader /> : rows.length === 0 ? <Empty icon={<Users />} title={t('respondents.none')} /> :
        <div className="table-wrap"><table className="table"><thead><tr>
          <th>{t('respondents.name')}</th><th>{t('respondents.external_id')}</th><th>{t('respondents.gender')}</th><th>{t('respondents.active')}</th><th />
        </tr></thead><tbody>{rows.map((row) => <tr key={row.id}>
          <td className="title-cell">{row.name}</td><td className="muted">{row.externalId || '—'}</td>
          <td>{t(`respondents.${row.gender}`)}</td><td><span className={`pill ${row.active ? 'green' : ''}`}>{t(row.active ? 'respondents.active' : 'respondents.inactive')}</span></td>
          <td className="num"><button className="btn sm" onClick={() => setEditing(row)}>{t('surveys.edit')}</button></td>
        </tr>)}</tbody></table></div>}
    </div>
    {(creating || editing) && <RespondentForm record={editing} onClose={() => { setCreating(false); setEditing(null); }} onSaved={async (record) => {
      try {
        if (editing) await api.updateRespondent(editing.id, record); else await api.createRespondent(record);
        toast(t(editing ? 'respondents.updated' : 'respondents.created'));
        setCreating(false); setEditing(null); await load();
      } catch (e) { toast(e instanceof ApiError ? e.message : t('common.action_failed'), 'error'); }
    }} />}
  </>;
}

function RespondentForm({ record, onClose, onSaved }: { record: RespondentRecord | null; onClose: () => void; onSaved: (r: Omit<RespondentRecord, 'id'>) => Promise<void> }) {
  const { t } = useI18n();
  const [name, setName] = useState(record?.name ?? '');
  const [externalId, setExternalId] = useState(record?.externalId ?? '');
  const [gender, setGender] = useState<RespondentGender>(record?.gender ?? 'unspecified');
  const [active, setActive] = useState(record?.active ?? true);
  const [busy, setBusy] = useState(false);
  async function submit(e: FormEvent) { e.preventDefault(); setBusy(true); await onSaved({ name, externalId, gender, active }); setBusy(false); }
  return <Modal title={record ? t('respondents.save') : t('respondents.add')} onClose={onClose} footer={<>
    <button className="btn" onClick={onClose}>{t('common.cancel')}</button>
    <button className="btn primary" form="respondent-form" disabled={busy}>{record ? t('respondents.save') : t('respondents.create')}</button>
  </>}>
    <form id="respondent-form" className="form-grid" onSubmit={(e) => void submit(e)}>
      <Field label={t('respondents.name')}><input className="input" required maxLength={200} value={name} onChange={(e) => setName(e.target.value)} /></Field>
      <Field label={t('respondents.external_id')}><input className="input" maxLength={100} value={externalId} onChange={(e) => setExternalId(e.target.value)} /></Field>
      <Field label={t('respondents.gender')}><select className="select" value={gender} onChange={(e) => setGender(e.target.value as RespondentGender)}>{genders.map((g) => <option key={g} value={g}>{t(`respondents.${g}`)}</option>)}</select></Field>
      {record && <label className="row gap-8"><input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} />{t('respondents.active')}</label>}
    </form>
  </Modal>;
}
